import React from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  App,
  Button,
  Col,
  Divider,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";

import { ItemSchemaGroup, MenuType } from "../../types/model";
import { zIndex } from "../../config/theme";
import { useAntdTheme, useNotification } from "../../shared/hooks";
import { PointsConfig } from "../points-config";
import { createComboAddonSchema, updateComboAddonSchema } from "./api";
import { MENU_TYPES, menuTypeLabel, tierLabel } from "./components";
import { OptionPricing } from "./OptionPricing";
import { TierCard } from "./TierCard";
import {
  OverrideInputs,
  addonForSave,
  duplicateTier,
  findNegativePriceErrors,
  indexMenuItems,
  moveInArray,
  moveItemToTier,
  newTierId,
  offeredModificationSchemas,
  overridesToInputs,
  tierOverriddenOptions,
} from "./pricing";
import { ComboAddonSchema, ComboAddonSchemaWithUsage, ComboAddonTier } from "./types";

const { Text, Title } = Typography;

export type ComboAddonModalProps = {
  open: boolean;
  restaurantId: string;
  // The add-on to edit, or undefined to create one.
  addon?: ComboAddonSchemaWithUsage;
  // Menu type of a new add-on.
  defaultMenuType: MenuType;
  // Disallow changing the menu type (e.g. when created from a combo, whose
  // section fixes it).
  lockMenuType?: boolean;
  // Every section of the menu; the editor shows those of the add-on's menu type.
  groups: ReadonlyArray<ItemSchemaGroup>;
  pointsConfig: PointsConfig | undefined;
  // Raise above another modal when opened from one.
  nested?: boolean;
  onClose: () => void;
  onSaved: (addon: ComboAddonSchemaWithUsage) => void;
};

export const ComboAddonModal: React.FC<ComboAddonModalProps> = (props) => {
  // Mount the editor only while open so every open starts from props.
  return props.open ? <ComboAddonEditor {...props} /> : null;
};

const emptyTier = (): ComboAddonTier => ({
  id: newTierId(),
  name: "",
  price: 0,
  points: null,
  seq: 0,
  items: [],
});

const ComboAddonEditor: React.FC<ComboAddonModalProps> = ({
  restaurantId,
  addon,
  defaultMenuType,
  lockMenuType,
  groups,
  pointsConfig,
  nested,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { modal } = App.useApp();
  const { createSuccess, updateSuccess, notifyError, showError } = useNotification();

  const [draft, setDraft] = React.useState<ComboAddonSchema>(() =>
    addon
      ? {
          ...addon,
          tiers: [...(addon.tiers ?? [])]
            .sort((a, b) => a.seq - b.seq)
            .map((tier) => ({
              ...tier,
              items: [...(tier.items ?? [])].sort((a, b) => a.seq - b.seq),
            })),
        }
      : {
          menu_type: defaultMenuType,
          name: "",
          intl_name: {},
          tiers: [emptyTier()],
          modification_prices: [],
        }
  );
  const [inputs, setInputs] = React.useState<OverrideInputs>(() =>
    overridesToInputs(addon?.modification_prices ?? [])
  );
  const [saving, setSaving] = React.useState(false);
  const [triedSave, setTriedSave] = React.useState(false);

  const usedBy = addon?.used_by ?? [];
  const menuTypeLocked = lockMenuType || usedBy.length > 0;

  const menuGroups = React.useMemo(
    () => groups.filter((g) => g.menu_type === draft.menu_type),
    [groups, draft.menu_type]
  );
  const items = React.useMemo(() => indexMenuItems(menuGroups), [menuGroups]);

  const saved = addonForSave(draft, inputs, items);
  const negativeErrors = findNegativePriceErrors(saved, items);
  const offeredSchemas = offeredModificationSchemas(draft.tiers, items);

  const setTiers = (update: (tiers: ComboAddonTier[]) => ComboAddonTier[]) =>
    setDraft((prev) => ({ ...prev, tiers: update(prev.tiers) }));

  const changeMenuType = (menuType: MenuType) => {
    const hasItems = draft.tiers.some((tier) => tier.items.length > 0);
    const apply = () =>
      setDraft((prev) => ({
        ...prev,
        menu_type: menuType,
        // Items belong to a menu type, so none of them carry over.
        tiers: prev.tiers.map((tier) => ({ ...tier, items: [] })),
      }));
    if (!hasItems) {
      apply();
      return;
    }
    modal.confirm({
      title: t("combo_change_menu_type_title"),
      content: t("combo_change_menu_type_content"),
      okText: t("yes"),
      cancelText: t("no"),
      zIndex: zIndex.modal + 20,
      onOk: apply,
    });
  };

  const setInput = (key: string, value: string | null) =>
    setInputs((prev) => {
      const next = { ...prev };
      if (value === null || value.trim() === "") {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });

  const resetChoice = (choiceId: string) =>
    setInputs((prev) => {
      const next: OverrideInputs = {};
      for (const [key, value] of Object.entries(prev)) {
        if (!key.endsWith(`|${choiceId}`)) {
          next[key] = value;
        }
      }
      return next;
    });

  const removeTier = (tierId: string) => {
    setTiers((tiers) => tiers.filter((tier) => tier.id !== tierId));
    setInputs((prev) => {
      const next: OverrideInputs = {};
      for (const [key, value] of Object.entries(prev)) {
        if (!key.startsWith(`${tierId}|`)) {
          next[key] = value;
        }
      }
      return next;
    });
  };

  const clientErrors: string[] = [];
  if (saved.name === "") {
    clientErrors.push(t("name_is_required"));
  }
  if (saved.tiers.length === 0) {
    clientErrors.push(t("combo_addon_needs_tier"));
  }
  saved.tiers.forEach((tier) => {
    if (tier.items.length === 0) {
      clientErrors.push(t("combo_tier_needs_item", { tier: tierLabel(t, tier) }));
    }
  });

  const handleSave = () => {
    setTriedSave(true);
    if (clientErrors.length > 0 || negativeErrors.length > 0) {
      showError(clientErrors[0] ?? t("combo_fix_option_prices"));
      return;
    }
    setSaving(true);
    const onError = (err: unknown) => {
      setSaving(false);
      notifyError(t("combo_failed_to_save_addon"), err);
    };
    if (addon?.id) {
      updateComboAddonSchema(
        restaurantId,
        addon.id,
        saved,
        () => {
          setSaving(false);
          updateSuccess(saved.name);
          onSaved({ ...saved, id: addon.id, used_by: usedBy });
        },
        onError
      );
    } else {
      createComboAddonSchema(
        restaurantId,
        saved,
        (id) => {
          setSaving(false);
          createSuccess(saved.name);
          onSaved({ ...saved, id, used_by: [] });
        },
        onError
      );
    }
  };

  return (
    <Modal
      open
      title={
        <Title level={4} style={{ margin: 0 }}>
          {addon ? t("combo_edit_addon") : t("combo_create_addon")}
        </Title>
      }
      onCancel={onClose}
      zIndex={nested ? zIndex.modal + 10 : zIndex.modal}
      width="min(1100px, calc(100vw - 48px))"
      style={{ top: 24 }}
      styles={{
        body: { maxHeight: "calc(100vh - 180px)", overflowY: "auto", paddingRight: 8 },
      }}
      footer={
        <Space>
          <Button onClick={onClose}>{t("cancel")}</Button>
          <Button
            type="primary"
            loading={saving}
            onClick={handleSave}
            disabled={negativeErrors.length > 0}
          >
            {t("save")}
          </Button>
        </Space>
      }
    >
      <Form layout="vertical" colon={false} component="div">
        <Row gutter={token.marginMD}>
          <Col xs={24} md={12}>
            <Form.Item
              label={t("name")}
              required
              validateStatus={triedSave && saved.name === "" ? "error" : undefined}
              help={triedSave && saved.name === "" ? t("name_is_required") : undefined}
            >
              <Input
                aria-label={t("name")}
                value={draft.name}
                placeholder={t("combo_addon_name_placeholder")}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={t("menu_type")}
              tooltip={
                usedBy.length > 0
                  ? t("combo_menu_type_locked", { combos: usedBy.map((c) => c.name).join(", ") })
                  : undefined
              }
            >
              <Select<MenuType>
                aria-label={t("menu_type")}
                value={draft.menu_type}
                disabled={menuTypeLocked}
                onChange={changeMenuType}
                options={MENU_TYPES.map((mt) => ({ value: mt, label: menuTypeLabel(t, mt) }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label={t("combo_chinese_name")}>
              <Input
                value={draft.intl_name?.zh ?? ""}
                onChange={(e) =>
                  setDraft({ ...draft, intl_name: { ...draft.intl_name, zh: e.target.value } })
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label={t("combo_spanish_name")}>
              <Input
                value={draft.intl_name?.es ?? ""}
                onChange={(e) =>
                  setDraft({ ...draft, intl_name: { ...draft.intl_name, es: e.target.value } })
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label={t("combo_english_name")}>
              <Input
                value={draft.intl_name?.en ?? ""}
                onChange={(e) =>
                  setDraft({ ...draft, intl_name: { ...draft.intl_name, en: e.target.value } })
                }
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Divider orientation="left" style={{ marginTop: 0 }}>
        {t("combo_tiers")}
      </Divider>
      <Text type="secondary" style={{ display: "block", marginBottom: token.marginSM }}>
        {t("combo_tiers_help")}
      </Text>
      {draft.tiers.map((tier, index) => (
        <TierCard
          key={tier.id}
          tier={tier}
          index={index}
          tiers={draft.tiers}
          groups={menuGroups}
          items={items}
          pointsConfig={pointsConfig}
          overriddenOptions={tierOverriddenOptions(saved, tier.id, items)}
          onChange={(next) => setTiers((tiers) => tiers.map((x) => (x.id === next.id ? next : x)))}
          onMove={(from, to) => setTiers((tiers) => moveInArray(tiers, from, to))}
          onDuplicate={() => {
            const result = duplicateTier(
              draft.tiers,
              inputs,
              tier.id,
              newTierId(),
              t("combo_copy_suffix")
            );
            setDraft({ ...draft, tiers: result.tiers });
            setInputs(result.inputs);
          }}
          onRemove={() => removeTier(tier.id)}
          onMoveItem={(itemId, toTierId) =>
            setTiers((tiers) => moveItemToTier(tiers, tier.id, itemId, toTierId))
          }
        />
      ))}
      <Button
        type="dashed"
        block
        icon={<PlusOutlined />}
        onClick={() => setTiers((tiers) => [...tiers, emptyTier()])}
      >
        {t("combo_add_tier")}
      </Button>

      <Divider orientation="left">{t("combo_option_pricing")}</Divider>
      <Text type="secondary" style={{ display: "block", marginBottom: token.marginSM }}>
        {t("combo_option_pricing_help")}
      </Text>
      {negativeErrors.length > 0 ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: token.marginSM }}
          message={t("combo_fix_option_prices")}
        />
      ) : null}
      {draft.tiers.every((tier) => tier.items.length === 0) ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("combo_add_items_first")} />
      ) : (
        <OptionPricing
          schemas={offeredSchemas}
          tiers={draft.tiers}
          inputs={inputs}
          errors={negativeErrors}
          onChange={setInput}
          onReset={resetChoice}
        />
      )}
      {triedSave && clientErrors.length > 0 ? (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: token.marginMD }}
          message={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {clientErrors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          }
        />
      ) : null}
    </Modal>
  );
};

// Names of the combos using an add-on, for tooltips.
export const usedByNames = (addon: ComboAddonSchemaWithUsage): string =>
  addon.used_by.map((c) => c.name).join(", ");
